import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import TrueImpactHighlightedTextOverlay from 'shared/design-system/components/TrueImpactHighlightedTextOverlay';
import { useDonorNameFlag } from './DonorNameFlagProvider';
import DonorNameWarning from './DonorNameWarning';
import { getDonorNameFieldLabel } from './donorNameFlagUtils';

interface DonorNameHighlightProps {
  text: string;
  questionId?: number;
  className?: string;
  children: React.ReactNode;
}

const HighlightWrapper = styled.div`
  position: relative;
  width: 100%;
`;

// The overlay has to sit exactly on top of the field's text, so its box and
// font metrics are copied from the rendered input instead of being guessed in
// CSS (padding differs between plain textareas and MUI inputs).
const getOverlayStyle = (
  wrapper: HTMLDivElement,
  field: HTMLElement,
): React.CSSProperties => {
  const styles = window.getComputedStyle(field);
  const fieldBox = field.getBoundingClientRect();
  const wrapperBox = wrapper.getBoundingClientRect();
  return {
    top: fieldBox.top - wrapperBox.top,
    left: fieldBox.left - wrapperBox.left,
    right: 'auto',
    bottom: 'auto',
    width: fieldBox.width,
    height: fieldBox.height,
    boxSizing: 'border-box',
    paddingTop: styles.paddingTop,
    paddingRight: styles.paddingRight,
    paddingBottom: styles.paddingBottom,
    paddingLeft: styles.paddingLeft,
    borderWidth: styles.borderWidth,
    borderStyle: 'solid',
    borderColor: 'transparent',
    fontFamily: styles.fontFamily,
    fontSize: styles.fontSize,
    fontWeight: styles.fontWeight,
    fontStyle: styles.fontStyle,
    lineHeight: styles.lineHeight,
    letterSpacing: styles.letterSpacing,
    textAlign: styles.textAlign as React.CSSProperties['textAlign'],
    textIndent: styles.textIndent,
    textTransform: styles.textTransform as React.CSSProperties['textTransform'],
  };
};

const DonorNameHighlight: React.FC<DonorNameHighlightProps> = ({
  text,
  questionId,
  className,
  children,
}) => {
  const { detect, fieldLabels } = useDonorNameFlag();
  const [scrollTop, setScrollTop] = useState<number>(0);
  const [overlayStyle, setOverlayStyle] = useState<React.CSSProperties>();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const ranges = detect(text);
  const hasRanges = ranges.length > 0;

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setScrollTop((event.target as HTMLElement).scrollTop || 0);
  }, []);

  const syncOverlayStyle = useCallback(() => {
    const wrapper = wrapperRef.current;
    const field = wrapper?.querySelector<HTMLElement>('textarea, input');
    if (!wrapper || !field) return;
    setOverlayStyle(getOverlayStyle(wrapper, field));
  }, []);

  useLayoutEffect(() => {
    if (hasRanges) syncOverlayStyle();
  }, [hasRanges, text, syncOverlayStyle]);

  useEffect(() => {
    if (!hasRanges) return undefined;
    window.addEventListener('resize', syncOverlayStyle);
    return () => window.removeEventListener('resize', syncOverlayStyle);
  }, [hasRanges, syncOverlayStyle]);

  const fieldLabel = getDonorNameFieldLabel(questionId);
  const warningLabels = Array.from(
    new Set(fieldLabel ? [...fieldLabels, fieldLabel] : fieldLabels),
  );

  return (
    <>
      <HighlightWrapper
        ref={wrapperRef}
        className={className}
        onScrollCapture={handleScroll}
      >
        {children}
        {overlayStyle && (
          <TrueImpactHighlightedTextOverlay
            text={text}
            ranges={ranges}
            scrollTop={scrollTop}
            style={overlayStyle}
            className="donor-name-overlay"
          />
        )}
      </HighlightWrapper>
      {hasRanges && <DonorNameWarning fieldLabels={warningLabels} />}
    </>
  );
};

export default DonorNameHighlight;
